import { ERROR_LEVELS, SENTRY_CABINET } from 'services/Sentry/constants';

import sentry from 'services/Sentry/SentryInstance';
import { IError } from 'services/Sentry/interfaces';

import { isProd } from 'helpers/utils';
import { clearLocalConfig } from './helpers';

import { ensureInitialized, fetchAndActivate, getRemoteConfig, RemoteConfig } from 'firebase/remote-config';

import { initializeApp, FirebaseApp } from 'firebase/app';
import { firebaseConfig, MAX_RETRIES, remoteConfigSettings } from './const';

let app: FirebaseApp | null = null;
let remoteConfig: RemoteConfig;

let resolveActivation: () => void;
let activationStarted = false;

const activationConfigPromise = new Promise<void>((resolve) => {
    resolveActivation = resolve;
});

const initFirebase = async (): Promise<void> => {
    if (activationStarted) return activationConfigPromise;

    activationStarted = true;

    try {
        app = initializeApp(firebaseConfig);
        remoteConfig = getRemoteConfig(app);
        remoteConfig.settings = remoteConfigSettings;

        await fetchAndActivateWithRetries(remoteConfig);

        if (!isProd()) clearLocalConfig();
    } catch (error) {
        sentry.logError(error as IError, SENTRY_CABINET, ERROR_LEVELS.CRITICAL);
    } finally {
        resolveActivation();
    }

    return activationConfigPromise;
};

const fetchAndActivateWithRetries = async (rc: RemoteConfig, retries = MAX_RETRIES): Promise<void> => {
    try {
        await ensureInitialized(rc);
        await fetchAndActivate(rc);

        resolveActivation();
    } catch (error) {
        if (retries > 0) {
            console.error(
                `Fetch and activate failed. Retrying... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`,
                error
            );

            await fetchAndActivateWithRetries(rc, retries - 1);
        } else {
            resolveActivation();
        }
    }
};

export { remoteConfig, activationConfigPromise, initFirebase };
